import React, { useRef, useState } from "react";
import { RCCButton, RCCGrid, RCCHookToast, RCCInput, RCCMicrosoftSocialLoginButton, RCCRefMicrosoftSocialLoginButton, RCCServicesApi, RCCUseMicrosoftSocialLogin } from "react-common-components";
import { useForm } from "react-hook-form";
import { Link, useHistory } from "react-router-dom";
import changeCompanyLogged from "src/components/Shared/Header/changeCompanyLogged";
import Title from "src/components/Title";
import { useAuth } from "src/context/Auth/context";
import { useLoaderBackdrop } from "src/context/LoaderBackdrop/context";
import getMicrosoftDomainOfEmail from "src/context/MicrosoftSocialLogin/utils";
import { useReloadAnimation } from "src/context/ReloadAnimation/context";
import { useTranslation } from "src/context/Translations/context";
import { getValidationErrors, standardErrorMessages } from "src/utils/errors";
import {IFormUsersInvite} from "./interface";
import schemaUserInvite from "./schemas";

const FormUserInvite: React.FC<IFormUsersInvite> = ({
	data
}) => {
	const {translate} = useTranslation();
	const {user, company, updateCompanyLogged} = useAuth();
	const {reload} = useReloadAnimation();
	const {addToast} = RCCHookToast();
	const { REACT_APP_ADDRESS } = process.env;
	const { updateMsalInstance } = RCCUseMicrosoftSocialLogin();
	const {
		handleSubmit, 
		register, 
		setError, 
		clearErrors, 
		formState: {errors},
		watch,
		setValue
	} = useForm();
	const {signIn} = useAuth();
	const history = useHistory();
	const { setLoading } = useLoaderBackdrop();
	const [isValid, setIsValid] = useState(data.domainLoginType === null);

	const [buttonLoading, setButtonLoading] = useState(false);
    
	const refSubmitFormButton = useRef<HTMLButtonElement>(null);
	const onSubmit = async (dt: any) => {
		try{
			clearErrors();
			if(dt.email !== data.efa026Email){
				addToast({
					color: "yellow",
					description: translate("Você está tentando se registrar com um email diferente do permitido para esse convite"),
					title: translate("Atenção")
				});
				return;
			}

			if(!isValid){
				addToast({
					color: "yellow",
					description: translate("Pendente validação do email"),
					title: translate("Atenção")
				});
				return;
			}
			
			setButtonLoading(true);

			if(data.domainLoginType === null)
				await schemaUserInvite(translate).validate(dt, {
					abortEarly: false
				});

			await RCCServicesApi.postRequest({
				method: "UserPublic/Register",
				body: {
					email: dt.email,
					name: dt.name,
					password: dt.password,
					efa001Id: data.efa026Efa001Id,
					efa004Id: data.efa026Efa004Id,
					hash: data.efa026Hash
				}
			});

			await signIn({
			    email: dt.email,
			    password: dt.password,
				remember: true,
				executeAfterTermsUseAccepted: () => history.push("/")
			});
		}catch(err){
			setButtonLoading(false);
			if(getValidationErrors(err, setError))
				return;

			standardErrorMessages(err, translate("Erro ao registrar usuário"), addToast);
		}	
	};

	const acceptInvitation = async () => {
		try{
			if(user.efa005Email !== data.efa026Email){
				addToast({
					color: "yellow",
					description: translate("Você está tentando se registrar com um email diferente do permitido para esse convite"),
					title: translate("Atenção")
				});
				return;
			}

			setButtonLoading(true);
			const response = await RCCServicesApi.postRequest({
				method: "User/AcceptInvite",
				body: {
					efa001Id: data.efa026Efa001Id,
					efa004Id: data.efa026Efa004Id,
					hash: data.efa026Hash
				}
			});
			setButtonLoading(false);
			addToast({
				color: "green",
				description: translate("Convite aceito"),
				title: translate("Sucesso")
			});
			if(!(company && company !== null)){
				await changeCompanyLogged(response.data, updateCompanyLogged, reload, translate, addToast, setLoading);
			}
			history.push("/");
		}catch(err){
			setButtonLoading(false);
			standardErrorMessages(err, translate("Erro ao aceitar convite"), addToast);
		}
	};

	const refMicrosoftSocialLoginButton = useRef<RCCRefMicrosoftSocialLoginButton>(null);
	const validateMicrosoft = async () => {
		const email = watch("email");

		if(!email){
			addToast({
				color: "yellow",
				description: translate("Preencha todos os campos para prosseguir"),
				title: translate("Atenção")
			});
			return;
		}

		const configs = await getMicrosoftDomainOfEmail(email, translate, addToast);
		if(!configs){
			refMicrosoftSocialLoginButton?.current?.resetLoading();
			return;
		}
		await updateMsalInstance(configs.clientId, configs.cloudInstance, configs.tenantInfo, REACT_APP_ADDRESS);
		try{
			refMicrosoftSocialLoginButton?.current?.setLoading();
			const dt = await refMicrosoftSocialLoginButton?.current?.handleLogin();
					
			if(!dt || !dt.account)
				throw new Error();

			setIsValid(true);
			setValue("name", dt.account.name);
			setValue("password", dt.account.localAccountId);
			setValue("password_confirmation", dt.account.localAccountId);
			refSubmitFormButton.current?.click();
		}catch(err){
			refMicrosoftSocialLoginButton?.current?.resetLoading();
			standardErrorMessages(err, "Erro ao efetuar login neste domínio", addToast);
		}
		

	};

	if(!user && data.userExists)
		return <>
			<Title color="defaultColor">
				{translate("Logue-se na aplicação para acessar o convite")}
			</Title>
			<Link to={`/login?redirect=${history.location.pathname}`}>Login</Link>
		</>;
        
	if(
		(user && !data.userExists) || 
		(user && user.efa005Email !== data.efa026Email)
	)
		return <>
			<Title color="defaultColor">
				{translate("Esse convite não é para o seu usuário")}
			</Title>			
		</>;

	if(user && data.userExists && user.efa005Email === data.efa026Email)
		return <>
			<Title color="defaultColor">
				{translate("Você foi convidado para participar do grupo")} {data.efa026Efa004Nome} {translate("no grupo empresarial")} {data.efa026Efa001Nome}
			</Title>
			<RCCButton 
				color="purple" 
				type="button"
				onClick={() => acceptInvitation()} 
				loading={buttonLoading}
				loadingText={translate("Carregando")}
				styleInline={`
								padding: 0 16px;
								width: 100%;
								height: 56px;
								>span.span-of-component-button{
									font-size: 1rem;
								}
							`}
			>{translate("Confirmar")}</RCCButton>
		</>;

	return (
		<form onSubmit={handleSubmit(onSubmit)}>
			<h1>{translate("Registrar")}</h1>
			<RCCGrid container item direction="column">
				<RCCGrid container item>
					<RCCInput 
						name="email"
						type="email"
						register={register}
						label={translate("Email")}
						maxLength={200}
						required
						error={errors.email}
						layout="outlined"
						autoComplete="off"
					/>
				</RCCGrid>
				{data.domainLoginType === null ? (
					<>
						<RCCGrid container item>
							<RCCInput 
								name="name"
								type="name"
								register={register}
								label={translate("Nome completo")}
								maxLength={60}
								required
								error={errors.name}
								layout="outlined"
								autoComplete="off"
							/>
						</RCCGrid>
						<RCCGrid container item>
							<RCCInput 
								name="password"
								type="password"
								register={register}
								label={translate("Senha")}
								maxLength={30}
								required
								error={errors.password}
								layout="outlined"
								autoComplete="off"
							/>
						</RCCGrid>
						<RCCGrid container item>
							<RCCInput 
								name="password_confirmation"
								type="password"
								register={register}
								label={translate("Confirmar senha")}
								maxLength={30}
								required
								error={errors.password_confirmation}
								layout="outlined"
								autoComplete="off"
							/>
						</RCCGrid>
						<RCCGrid container item>
							<RCCButton 
								color="purple" 
								type="submit"
								loading={buttonLoading}
								loadingText={translate("Carregando")}
								styleInline={`
										padding: 0 16px;
										width: 100%;
										height: 56px;
										>span.span-of-component-button{
											font-size: 1rem;
										}
									`}
							>{translate("Confirmar")}</RCCButton>
						</RCCGrid>
					</>
				): (
					<button 
						ref={refSubmitFormButton}
						type="submit"
						aria-label="Submit Form"
						style={{ display: "none" }}
					></button>
				)}
				{data.domainLoginType === 1 && (
					<RCCMicrosoftSocialLoginButton 
						ref={refMicrosoftSocialLoginButton}
						onClick={validateMicrosoft}
						styleInline={`
							padding: 0 16px;
							width: 100%;
							height: 56px;
							>span.span-of-component-button{
								font-size: 1rem;
							}
						`}
					>
						{translate("Entrar com a Microsoft")}
					</RCCMicrosoftSocialLoginButton>
				)}
			</RCCGrid>
		</form>
	);
};

export default FormUserInvite;
